/**
 * Phase E E.6 — Modal upload devis + extraction Claude Vision.
 *
 * Endpoint backend reel : POST /api/chantiers/{cid}/devis/upload
 * (et non pas /upload-and-extract comme dans la spec, mais idem comportement).
 */
import { useState } from 'react';
import axios from 'axios';
import { X, FilePdf, CheckCircle } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DevisUploadDialog({ chantierId, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/devis/upload`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        },
      );
      setResult(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Erreur upload');
    } finally {
      setUploading(false);
    }
  };

  // Le backend retourne `extracted` (pas `extracted_data`) + `devis` resume
  const extracted = result?.extracted || {};
  const devis = result?.devis || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <FilePdf size={24} /> Uploader un devis
          </h2>
          <button onClick={onClose} data-testid="devis-upload-close"><X size={24} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {!result && (
            <>
              <p className="text-sm text-gray-600">
                Importez un devis au format PDF / image. L'Assistant extrait automatiquement
                le fournisseur, la date, le numéro, les montants HT/TVA/TTC.
              </p>
              <input
                type="file"
                accept="application/pdf,image/*"
                onChange={(e) => setFile(e.target.files[0])}
                className="border rounded p-2 w-full text-sm"
                data-testid="devis-upload-file"
              />
              {file && (
                <p className="text-sm text-green-700">
                  📄 {file.name} ({(file.size / 1024).toFixed(0)} Ko)
                </p>
              )}
            </>
          )}

          {result && (
            <div className="space-y-3">
              <div
                className={`border rounded-lg p-4 flex items-start gap-3 ${
                  result.success
                    ? 'bg-green-50 border-green-200'
                    : 'bg-orange-50 border-orange-200'
                }`}
              >
                <CheckCircle size={24} className={result.success ? 'text-green-600' : 'text-orange-600'} />
                <div>
                  <p className="font-semibold">
                    {result.success ? 'Extraction réussie' : 'Devis créé, extraction partielle'}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    Devis ID : <code className="text-xs">{result.devis_id || '—'}</code>
                  </p>
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-3 py-2 font-medium text-sm">Données extraites</div>
                <div className="divide-y text-sm">
                  <Row label="Numéro" value={extracted.numero || devis.numero} />
                  <Row label="Fournisseur" value={(extracted.emetteur && extracted.emetteur.nom) || devis.entreprise} />
                  <Row label="Date émission" value={extracted.date_emission} />
                  <Row label="Validité" value={extracted.date_validite} />
                  <Row label="Lot" value={extracted.lot_suggere || devis.lot} />
                  <Row
                    label="Total HT"
                    value={extracted.montant_ht ? `${Number(extracted.montant_ht).toLocaleString('fr-FR')} €` : '—'}
                  />
                  <Row label="TVA" value={extracted.tva_taux ? `${extracted.tva_taux}%` : '—'} />
                  <Row
                    label="Total TTC"
                    value={extracted.montant_ttc ? `${Number(extracted.montant_ttc).toLocaleString('fr-FR')} €` : '—'}
                  />
                </div>
              </div>

              {Array.isArray(extracted.lignes) && extracted.lignes.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-3 py-2 font-medium text-sm">
                    Lignes ({extracted.lignes.length})
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-xs text-gray-600">
                        <tr>
                          <th className="text-left p-2">Poste</th>
                          <th className="text-left p-2">Unité</th>
                          <th className="text-right p-2">Qté</th>
                          <th className="text-right p-2">PU HT</th>
                          <th className="text-right p-2">Total HT</th>
                        </tr>
                      </thead>
                      <tbody>
                        {extracted.lignes.slice(0, 10).map((l, i) => (
                          <tr key={i} className="border-t">
                            <td className="p-2">{l.poste}</td>
                            <td className="p-2">{l.unite}</td>
                            <td className="p-2 text-right">{l.quantite}</td>
                            <td className="p-2 text-right">{l.pu_ht}</td>
                            <td className="p-2 text-right">{l.total_ht}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
              ⚠️ {error}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded">
            {result ? 'Fermer' : 'Annuler'}
          </button>
          {!result && (
            <button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg disabled:opacity-50"
              data-testid="devis-upload-submit"
            >
              {uploading ? 'Upload + extraction IA…' : 'Uploader et extraire'}
            </button>
          )}
          {result && (
            <button
              onClick={onSuccess}
              className="px-3 py-1.5 bg-green-600 text-white rounded-lg"
            >
              Voir le devis
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="px-3 py-2 flex justify-between gap-3">
      <span className="text-gray-600">{label}</span>
      <span className="font-medium text-right">{value || '—'}</span>
    </div>
  );
}
